import { sleep } from '~common/utils/sleep'
import type {
    ImporterConfig,
    ImporterEmailSearchPageProcessor,
    ImportStatus,
    PersistedImportState,
} from './types'
import type { StatePersister } from '~/features/state-persistence/types'
import type { Services } from '~/services/types'
import { DEFAULT_IMPORTER_CONFIG, DEFAULT_IMPORTER_STATE } from './constants'
import { convertImporterDateRange } from './utils'
import { DEFAULT_INVOICE_KEYWORDS } from '~common/invoice-processing/crude-classification'

export interface ImporterServiceDeps {
    getServices: () => Pick<
        Services,
        'invoiceProcessor' | 'errorTracker' | 'globalEvents'
    >
    importStatePersister: StatePersister<PersistedImportState>
    processSearchPage: ImporterEmailSearchPageProcessor
}

export class ImporterService {
    private processingPromise: Promise<void> | null = null
    private runController: AbortController | null = null

    constructor(private readonly deps: ImporterServiceDeps) {}

    async start(config?: ImporterConfig): Promise<void> {
        let state: PersistedImportState

        // Continue from existing state if it exists, regardless of config
        const existingState = await this.readState()
        const scanStatus = existingState?.status.scan ?? 'idle'
        const importStatus = existingState?.status.import ?? 'idle'
        const isScanError = scanStatus === 'error' || importStatus === 'error'
        if (
            importStatus === 'running' ||
            existingState?.status.scan === 'running'
        ) {
            return
        }
        if (config) {
            // ... else start a new run from scratch with provided config
            this.assertValidConfig(config)

            state = {
                ...DEFAULT_IMPORTER_STATE,
                config,
                status: {
                    paused: false,
                    scan: 'running',
                    import: 'running',
                },
                startedAt: Date.now(),
                lastUpdatedAt: Date.now(),
            }
            if (config?.resetProgress) {
                state = {
                    ...state,
                    nextPageToken: undefined,
                    lastMessageTimestamp: null,
                    actualInvoicesFoundThisRun: 0,
                    invoiceCandidatesSeenThisRun: 0,
                    invoiceCandidatesProcessedThisRun: 0,
                }
            }
        } else if (existingState) {
            if (isScanError) {
                state = {
                    ...existingState,
                    errorMessage: null,
                    status: {
                        paused: false,
                        scan: 'running',
                        import: 'running',
                    },
                }
            } else if (existingState.status.paused) {
                state = {
                    ...existingState,
                    status: {
                        paused: false,
                        scan:
                            scanStatus === 'completed'
                                ? 'completed'
                                : 'running',
                        import: 'running',
                    },
                }
            } else {
                state = {
                    ...existingState,
                    status: {
                        paused: false,
                        scan: 'running',
                        import: 'running',
                    },
                    startedAt: existingState.startedAt ?? Date.now(),
                    lastMessageTimestamp: null,
                    actualInvoicesFoundThisRun: 0,
                    invoiceCandidatesSeenThisRun: 0,
                    invoiceCandidatesProcessedThisRun: 0,
                }
            }
        } else {
            state = {
                ...DEFAULT_IMPORTER_STATE,
                status: {
                    paused: false,
                    scan: 'running',
                    import: 'running',
                },
                startedAt: Date.now(),
                lastUpdatedAt: Date.now(),
            }
        }

        if (!config?.terms || config?.terms.length === 0) {
            const terms: string[] = []
            Object.entries(DEFAULT_INVOICE_KEYWORDS).map(([language, term]) => {
                const currentLanguage =
                    config?.selectedLanguages.find(
                        (lang) => lang === language,
                    ) ?? 'en'
                if (currentLanguage === language) {
                    terms.push(...term)
                }
            })
            if (config) {
                config.terms = terms.join(' OR ')
            }
        }

        await this.writeState(state)

        this.runController = new AbortController()

        // Fire-and-forget the processing loop, but keep a reference and handle errors to avoid unhandled rejections
        this.processingPromise = this.processLoop(
            state,
            this.runController.signal,
        )
            .catch(async (error) => {
                const currentState = (await this.readState()) ?? state
                await this.writeState({
                    ...currentState,
                    errorMessage:
                        currentState?.errorMessage ??
                        (error instanceof Error
                            ? error.message
                            : 'Unexpected importer error'),
                    lastUpdatedAt: Date.now(),
                })
            })
            .finally(() => {
                this.processingPromise = null
            })
    }

    async pause(): Promise<void> {
        const state = await this.readState()
        if (!state) return

        const services = this.deps.getServices()

        await this.writeState({
            ...state,
            status: {
                paused: true,
                scan:
                    state.status.scan === 'running'
                        ? 'idle'
                        : (state.status.scan as ImportStatus),
                import:
                    state.status.import === 'running'
                        ? 'idle'
                        : (state.status.import as ImportStatus),
            },
            lastUpdatedAt: Date.now(),
        })
        services.invoiceProcessor.invoiceProcessingQueue.pause()
        if (this.runController) {
            this.runController.abort()
            this.runController = null
        }
    }

    async reset(): Promise<void> {
        if (this.runController) {
            this.runController.abort()
            this.runController = null
        }

        const state = await this.readState()
        if (state) {
            const newState = {
                ...state,
                status: {
                    paused: false,
                    scan: 'idle',
                    import: 'idle',
                },
                nextPageToken: undefined,
                errorMessage: null,
                startedAt: null,
                lastUpdatedAt: null,
                lastMessageTimestamp: null,
                totalInvoiceCandidates: 0,
                actualInvoicesFoundThisRun: 0,
                invoiceCandidatesSeenThisRun: 0,
                invoiceCandidatesProcessedThisRun: 0,
            }
            await this.writeState(newState as PersistedImportState)
        }
    }

    private async processLoop(
        state: PersistedImportState,
        signal: AbortSignal,
    ): Promise<void> {
        if (!this.deps || !this.deps.getServices) {
            throw new Error(
                'ImporterService deps not initialized - check service creation',
            )
        }

        const services = this.deps.getServices()
        const pageSize = Math.max(
            1,
            Math.min(100, state.config.pageSize ?? 100),
        )
        let shouldPause = false

        while (!signal.aborted) {
            try {
                state = (await this.readState()) ?? state

                const dateRange = convertImporterDateRange(
                    state.config.dateRange,
                )

                const result = await this.deps.processSearchPage({
                    pageSize,
                    dateRange,
                    terms: state.config.terms,
                    nextPageToken: state.nextPageToken,
                })

                // Update state with new search results
                const invoiceEmails = result.processedEmails.filter(
                    (email) => email.invoiceClassification === 'invoice',
                )
                state = (await this.readState()) ?? state
                state.nextPageToken = result.nextPageToken ?? undefined

                // Find the smallest receivedDate from all preProcessedEmails
                if (result.preProcessedEmails.length > 0) {
                    const smallestReceivedDate = Math.min(
                        ...result.preProcessedEmails.map(
                            (email) => email.receivedDate,
                        ),
                    )
                    // Only update if lastMessageTimestamp is null or if the smallest date is smaller
                    if (
                        state.lastMessageTimestamp === null ||
                        smallestReceivedDate < state.lastMessageTimestamp
                    ) {
                        state.lastMessageTimestamp = smallestReceivedDate
                    }
                }

                state.totalInvoiceCandidates += invoiceEmails.length
                state.invoiceCandidatesSeenThisRun += invoiceEmails.length

                const isDone = result.nextPageToken === null
                if (isDone) {
                    state.status.scan = 'completed'
                }
                await this.writeState(state)

                if (signal.aborted || isDone) {
                    break
                }

                // TODO-audit: Why is this here?
                await sleep(500)
            } catch (error) {
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : 'Failed to list Gmail messages'

                services.errorTracker.showErrorNotification(
                    'Failure in email importer process loop: ' + errorMessage,
                )
                // Persist error and pause to avoid hot-looping
                const currentState = (await this.readState()) ?? state
                await this.writeState({
                    ...currentState,
                    status: {
                        paused: false,
                        scan: 'error',
                        import: currentState.status.import,
                    },
                    errorMessage,
                    lastUpdatedAt: Date.now(),
                })
                shouldPause = true
                break
            }
        }
        this.runController = null

        if (shouldPause) {
            await this.pause()
        } else if (!signal.aborted) {
            // Successfully completed
            const currentState = (await this.readState()) ?? state
            await this.writeState({
                ...currentState,
                lastUpdatedAt: Date.now(),
            })
        }
    }

    private assertValidConfig(config: ImporterConfig): void {
        if (
            typeof config.dateRange.fromYear === 'number' &&
            typeof config.dateRange.toYear === 'number' &&
            typeof config.dateRange.fromMonth === 'number' &&
            typeof config.dateRange.toMonth === 'number'
        ) {
            return
        }
        const services = this.deps.getServices()
        services.errorTracker.throwErrorWithNotification(
            'Importer requires a date range: year or startMs/endMs',
        )
    }

    async getState(): Promise<PersistedImportState> {
        let state = await this.readState()
        if (!state) {
            state = DEFAULT_IMPORTER_STATE
        }

        return state
    }

    /** Resolves when there is no active run (useful for tests or shutdown). */
    async whenIdle(): Promise<void> {
        if (!this.processingPromise) return
        await this.processingPromise
    }

    private async readState(): Promise<PersistedImportState | null> {
        return this.deps.importStatePersister.hydrate()
    }

    private async writeState(state: PersistedImportState): Promise<void> {
        this.deps
            .getServices()
            .globalEvents.emit({ type: 'updateImportState', state })
        await this.deps.importStatePersister.persist(state)
    }
}
